import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, X, Copy, RefreshCw, Loader2 } from "lucide-react";

import { API, SENDING_ON } from "../lib/portalMode";

const SCHEDULE_ITEMS = {
  adult: [
    { item_number: "15_054_0128_1_3", description: "Therapy - Psychologist - Direct Service", rate: 252.99 },
    { item_number: "15_054_0128_1_3_CA", description: "Therapy - Psychologist - Cancellation", rate: 252.99 },
    { item_number: "15_054_0128_1_3_NF", description: "Therapy - Psychologist - Non-Face-to-Face", rate: 252.99 },
    { item_number: "15_054_0128_1_3_PT", description: "Therapy - Psychologist - Provider Travel", rate: 126.5 },
    { item_number: "15_054_0128_1_3_RR", description: "Therapy - Psychologist - NDIA Requested Reports", rate: 252.99 },
    { item_number: "15_054_0128_1_3_TH", description: "Therapy - Psychologist - Telehealth", rate: 252.99 },
  ],
  "early-childhood": [
    { item_number: "15_001_0118_1_3", description: "ECI Professional - Psychologist - Direct Service", rate: 252.99 },
    { item_number: "15_001_0118_1_3_CA", description: "ECI Professional - Psychologist - Cancellation", rate: 252.99 },
    { item_number: "15_001_0118_1_3_NF", description: "ECI Professional - Psychologist - Non-Face-to-Face", rate: 252.99 },
    { item_number: "15_001_0118_1_3_PT", description: "ECI Professional - Psychologist - Provider Travel", rate: 126.5 },
    { item_number: "15_001_0118_1_3_RR", description: "ECI Professional - Psychologist - NDIA Requested Reports", rate: 252.99 },
    { item_number: "15_001_0118_1_3_TH", description: "ECI Professional - Psychologist - Telehealth", rate: 252.99 },
  ],
};

const emptyRow = () => ({ item: "", description: "", hours: "", rate: "", note: "" });

export default function PrepareAgreement({ authToken, onClose }) {
  const [agreementType, setAgreementType] = useState("adult");
  const [clientName, setClientName] = useState("");
  const [period, setPeriod] = useState("");
  const [rows, setRows] = useState([emptyRow()]);
  const [links, setLinks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [generated, setGenerated] = useState(null);

  const headers = { headers: { Authorization: `Bearer ${authToken}` } };
  const items = SCHEDULE_ITEMS[agreementType];

  const loadLinks = () => {
    axios.get(`${API}/portal/service-agreements`, headers).then((r) => setLinks(r.data)).catch(() => {});
  };
  useEffect(loadLinks, []); // eslint-disable-line

  const rowTotal = (r) => {
    const h = parseFloat(r.hours) || 0;
    const rt = parseFloat(r.rate) || 0;
    return h * rt;
  };
  const grandTotal = rows.reduce((s, r) => s + rowTotal(r), 0);

  const updateRow = (idx, patch) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const pickItem = (idx, itemNumber) => {
    const found = items.find((i) => i.item_number === itemNumber);
    updateRow(idx, { item: itemNumber, description: found ? found.description : "", rate: found ? found.rate : "" });
  };

  const create = async () => {
    if (!SENDING_ON) return; // off the live portal nothing is sent (lib/portalMode.js)
    if (!clientName.trim()) { toast.error("Enter a client name."); return; }
    const payloadRows = rows.filter((r) => r.item).map((r) => ({
      item: r.item, description: r.description,
      hours: parseFloat(r.hours) || 0, rate: parseFloat(r.rate) || 0, note: r.note,
    }));
    if (!payloadRows.length) { toast.error("Add at least one support row."); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/portal/service-agreements`,
        { agreement_type: agreementType, client_name: clientName, agreement_period: period, rows: payloadRows },
        headers);
      const link = `${window.location.origin}/${res.data.link}`;
      setGenerated({ ...res.data, fullLink: link });
      toast.success("Client link created.");
      loadLinks();
    } catch (e) {
      toast.error("Could not create the link.");
    } finally {
      setSaving(false);
    }
  };

  const copyLink = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Link copied.");
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto p-4" data-testid="prepare-agreement-modal">
      <div className="bg-white rounded-xl max-w-3xl w-full my-6 shadow-xl">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">Prepare service agreement</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="prepare-close-btn"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Agreement type</Label>
              <select className="w-full border border-slate-300 rounded-md px-3 py-2 mt-1"
                value={agreementType}
                onChange={(e) => { setAgreementType(e.target.value); setRows([emptyRow()]); }}
                data-testid="prepare-type-select">
                <option value="adult">Adult</option>
                <option value="early-childhood">Early Childhood</option>
              </select>
            </div>
            <div>
              <Label>Client name</Label>
              <Input value={clientName} onChange={(e) => setClientName(e.target.value)}
                placeholder="For your reference and the PDF file name" className="mt-1"
                data-testid="prepare-client-name" />
            </div>
          </div>

          <div>
            <Label>Planned supports</Label>
            <div className="space-y-3 mt-2">
              {rows.map((r, idx) => (
                <div key={idx} className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`prepare-row-${idx}`}>
                  <div className="flex gap-2 items-start">
                    <select className="flex-1 border border-slate-300 rounded-md px-2 py-2 text-sm"
                      value={r.item} onChange={(e) => pickItem(idx, e.target.value)}
                      data-testid={`prepare-row-item-${idx}`}>
                      <option value="">Select support item…</option>
                      {items.map((i) => (
                        <option key={i.item_number} value={i.item_number}>{i.item_number} - {i.description}</option>
                      ))}
                    </select>
                    {rows.length > 1 && (
                      <button onClick={() => setRows((p) => p.filter((_, i) => i !== idx))}
                        className="text-red-500 p-2" data-testid={`prepare-row-remove-${idx}`}><X size={16} /></button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <span className="text-xs text-slate-500">Hours</span>
                      <Input type="number" step="0.25" min="0" value={r.hours}
                        onChange={(e) => updateRow(idx, { hours: e.target.value })}
                        data-testid={`prepare-row-hours-${idx}`} />
                    </div>
                    <div>
                      <span className="text-xs text-slate-500">Rate/hr</span>
                      <Input type="number" step="0.01" min="0" value={r.rate}
                        onChange={(e) => updateRow(idx, { rate: e.target.value })}
                        data-testid={`prepare-row-rate-${idx}`} />
                    </div>
                    <div>
                      <span className="text-xs text-slate-500">Total</span>
                      <div className="px-3 py-2 bg-slate-50 rounded-md text-sm font-medium" data-testid={`prepare-row-total-${idx}`}>
                        ${rowTotal(r).toFixed(2)}
                      </div>
                    </div>
                  </div>
                  <Input value={r.note} onChange={(e) => updateRow(idx, { note: e.target.value })}
                    placeholder="Note (optional), e.g. Fortnightly 1-hour sessions"
                    data-testid={`prepare-row-note-${idx}`} />
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" className="mt-2" onClick={() => setRows((p) => [...p, emptyRow()])}
              data-testid="prepare-add-row">
              <Plus size={15} className="mr-1" /> Add row
            </Button>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex-1 mr-4">
              <Label>Agreement period (optional)</Label>
              <Input value={period} onChange={(e) => setPeriod(e.target.value)}
                placeholder="e.g. October 2026 to March 2027" className="mt-1"
                data-testid="prepare-period" />
            </div>
            <div className="text-right">
              <div className="text-xs text-slate-500">Grand total</div>
              <div className="text-xl font-semibold text-slate-800" data-testid="prepare-grand-total">${grandTotal.toFixed(2)}</div>
            </div>
          </div>

          <Button onClick={create} disabled={saving || !SENDING_ON} className="w-full" data-testid="prepare-create-btn"
            style={{ backgroundColor: "#2060E8", color: "#fff" }}>
            {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Plus size={16} className="mr-2" />}
            Create client link
          </Button>

          {generated && (
            <div className="border border-green-200 bg-green-50 rounded-lg p-3" data-testid="prepare-generated-link">
              <div className="text-sm font-medium text-slate-700 mb-1">Client link (single use, expires in 60 days):</div>
              <div className="flex gap-2 items-center">
                <code className="flex-1 text-xs bg-white p-2 rounded border border-slate-200 break-all">{generated.fullLink}</code>
                <Button size="sm" variant="outline" onClick={() => copyLink(generated.fullLink)} data-testid="prepare-copy-btn">
                  <Copy size={14} />
                </Button>
              </div>
            </div>
          )}

          {links.length > 0 && (
            <div>
              <Label>Previously prepared links</Label>
              <div className="mt-2 space-y-2 max-h-56 overflow-y-auto">
                {links.map((l) => (
                  <div key={l.id} className="flex items-center justify-between text-sm border border-slate-200 rounded-md px-3 py-2" data-testid={`prepare-existing-${l.id}`}>
                    <div>
                      <span className="font-medium">{l.client_name || "-"}</span>
                      <span className="text-slate-500"> · {l.agreement_type} · ${Number(l.grand_total).toFixed(2)}</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${l.status === "signed" ? "bg-blue-100 text-blue-700" : l.status === "expired" ? "bg-slate-200 text-slate-600" : "bg-green-100 text-green-700"}`}>
                      {l.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
